import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    Output,
    Signal,
    WritableSignal,
    computed,
    effect,
    viewChild,
    viewChildren,
} from '@angular/core';
import { FuseConfig, FuseConfigService, Scheme } from '@fuse/services/config';
import { BusinessProcessTask } from 'app/api';
import html2canvas from 'html2canvas';
import { Subject, takeUntil } from 'rxjs';
import { BabylonChartConfiguration } from '../../babylonjs/core/babylon-chart-config.model';
import { BabylonContextMenuItem } from '../../models/babylon-context-menu-item';
import { MovingType, SensitivityType } from '../../models/moving-type';
import { PrintService } from '../../print.service';

@Component({
    selector: 'lib-svg-flowchart',
    templateUrl: './svg-flowchart.component.html',
    styleUrl: './svg-flowchart.component.scss',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SvgFlowchartComponent implements OnDestroy {
    protected destroyed$ = new Subject<void>();

    containerDiv: Signal<ElementRef> = viewChild('containerDiv');

    startRef: Signal<ElementRef> = viewChild('start');
    endRef: Signal<ElementRef> = viewChild('end');
    taskRefs = viewChildren<ElementRef>('task');

    @Input() movingType: WritableSignal<MovingType>;

    /** The scale of the camera */
    @Input() cameraScale: WritableSignal<string>;

    @Input() sensitivityType: WritableSignal<SensitivityType>;

    @Input() config: BabylonChartConfiguration;

    /** An EventEmitter which emits an event if a clickable Mesh is clicked */
    @Output() meshClicked: EventEmitter<string> = new EventEmitter();
    /** An EventEmitter which emits an event if a clickable Mesh is clicked */
    @Output() meshDoubleClicked: EventEmitter<string> = new EventEmitter();
    /** The BusinessProcessTasks that should be rendered */
    @Input() processes: Signal<BusinessProcessTask[]>;

    @Input() isPrintPreview: boolean = false;

    tasks: Signal<BusinessProcessTask[]> = computed(() => this.updateProcesses(this.processes()));

    /** The type of the flowchart */
    @Input() type: 'flowchart' | 'flowchart2';
    /** The id of the selected BusinessProcessTask */
    @Input() selectedId?: string;
    private _selectedId?: string;

    @Input() menuItems: Signal<BabylonContextMenuItem[]>;
    @Input() coloredTaskIds: Signal<string[]> | undefined;

    @Input() isPrinting: boolean = false;

    clickTimeout: any;

    private previousProcesses: BusinessProcessTask[] = [];

    constructor(
        public printService: PrintService,
        protected fuseConfigService: FuseConfigService
    ) {
        this.subscribeToPrint();
        this.printService.reSubscribe.pipe(takeUntil(this.destroyed$)).subscribe(() => {
            this.subscribeToPrint();
        });
        this.fuseConfigService.config$
            .pipe(takeUntil(this.destroyed$))
            .subscribe((config: FuseConfig) => {
                // Store the config
                this.scheme = config.scheme;
            });
    }

    ngOnDestroy(): void {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    trackByTask(index: number, task: BusinessProcessTask): string {
        return `${index}-${task.id}-${task['forceUpdate']}-${task.data.action.title1}-${task.data.action.title2}-${task.data.action.no}-${task.data.action.responsible}-${task.data.action.system}-${task.data.input}-${task.data.output}-${task.data.action.dependencies}-${task.data.action.category}-${task.data.info?.['tcode']}`;
    }

    private detectSwaps(newProcesses: BusinessProcessTask[]): Set<number> {
        if (!this.previousProcesses.length) {
            // First-time setup, store current state
            this.previousProcesses = [...newProcesses];
            return new Set();
        }

        const swappedIndexes = new Set<number>();

        // Compare each element with the previous state
        for (let i = 0; i < newProcesses.length - 1; i++) {
            // If two adjacent elements swapped positions, add them and their neighbor to the set
            if (
                this.previousProcesses[i]?.id === newProcesses[i + 1]?.id &&
                this.previousProcesses[i + 1]?.id === newProcesses[i]?.id
            ) {
                swappedIndexes.add(i - 1); // Previous element
                swappedIndexes.add(i); // First swapped element
                swappedIndexes.add(i + 1); // Second swapped element
                swappedIndexes.add(i + 2); // Next element
            }
        }

        return swappedIndexes;
    }

    private detectDependencyChanges(newTasks: BusinessProcessTask[]): Set<number> {
        const swappedIndexes = new Set<number>();

        newTasks.forEach((newTask, indexOfNewTask) => {
            const previousTask = this.previousProcesses.find(p => p.id === newTask.id);
            if (!previousTask) return;

            if (
                !this.areDependenciesEqual(
                    previousTask.data.action.dependencies,
                    newTask.data.action.dependencies
                )
            ) {
                swappedIndexes.add(indexOfNewTask);
                this.addDependencyIndexes(
                    newTasks,
                    newTask.data.action.dependencies,
                    indexOfNewTask,
                    swappedIndexes
                );
                this.addDependencyIndexes(
                    newTasks,
                    previousTask.data.action.dependencies,
                    indexOfNewTask,
                    swappedIndexes
                );
            }
        });

        return swappedIndexes;
    }

    private areDependenciesEqual(dep1: any[], dep2: any[]): boolean {
        return JSON.stringify(dep1) === JSON.stringify(dep2);
    }

    private addDependencyIndexes(
        newTasks: BusinessProcessTask[],
        dependencies: any[],
        indexOfNewTask: number,
        swappedIndexes: Set<number>
    ): void {
        dependencies.forEach(dependency => {
            const indexOfDependencyTask = newTasks.findIndex(p => p.id === dependency['id']);
            if (indexOfDependencyTask === -1) return;

            swappedIndexes.add(indexOfDependencyTask);

            const [minIndex, maxIndex] = [indexOfNewTask, indexOfDependencyTask].sort(
                (a, b) => a - b
            );
            for (let i = minIndex + 1; i < maxIndex; i++) {
                swappedIndexes.add(i);
            }
        });
    }

    updateProcesses(newProcesses: BusinessProcessTask[]): BusinessProcessTask[] {
        // Find which indexes need updating
        const affectedIndexes1 = this.detectSwaps(newProcesses);
        const affectedIndexes2 = this.detectDependencyChanges(newProcesses);

        const affectedIndexes = new Set([...affectedIndexes1, ...affectedIndexes2]);

        let result: BusinessProcessTask[] = [];
        if (affectedIndexes.size > 0) {
            // Force Angular to re-render only the affected indexes

            result = newProcesses.map((task, index) =>
                affectedIndexes.has(index)
                    ? { ...task, forceUpdate: Math.random() } // Clone object to trigger re-render
                    : task
            );
        } else {
            // Normal update
            result = newProcesses;
        }

        // Store the new array order
        this.previousProcesses = [...result];

        return result;
    }

    onClick(event: MouseEvent, task: BusinessProcessTask): void {
        event.stopPropagation();
        if (this.clickTimeout) {
            clearTimeout(this.clickTimeout);
        }

        this.clickTimeout = setTimeout(() => {
            this.meshClicked.emit(task.id);
            this.clickTimeout = null; // Clear the timer
        }, 250); // Adjust the timeout value if needed
    }
    onDoubleClick(event: MouseEvent, task: BusinessProcessTask): void {
        event.stopPropagation();
        if (this.clickTimeout) {
            clearTimeout(this.clickTimeout);
            this.clickTimeout = null;
        }
        this.meshDoubleClicked.emit(task.id);
    }

    /** Subscribing for print event */
    private subscribeToPrint(): void {
        this.printService.printScene.pipe(takeUntil(this.destroyed$)).subscribe(() => {
            this._selectedId = `${this.selectedId}`;
            this.selectedId = undefined;
            setTimeout(() => {
                this.convertDivToImage();
            }, 10);
        });

        this.printService.printDetailedScene.pipe(takeUntil(this.destroyed$)).subscribe(() => {
            this._selectedId = `${this.selectedId}`;
            this.selectedId = undefined;
            setTimeout(() => {
                this.convertDivToImages();
            }, 10);
        });
    }

    scheme: 'dark' | 'light' | 'auto' = 'light';
    originalScheme: 'dark' | 'light' | 'auto';
    printing: boolean = false;

    changeScheme() {
        if (this.scheme === 'light') this.scheme = 'dark';
        else this.scheme = 'light';
        this.setScheme(this.scheme);
    }

    /**
     * Set the scheme on the config
     *
     * @param scheme
     */
    setScheme(scheme: Scheme): void {
        this.fuseConfigService.config = { scheme };
        this.updateConfigInLocalStorage('scheme', scheme);
    }

    /**
     * Sync with local storage
     *
     */
    updateConfigInLocalStorage(key: string, value: any) {
        let config: any = localStorage.getItem('fuseConfig') ?? '{}';
        try {
            config = JSON.parse(config);
            config[key] = value;
            localStorage.setItem('fuseConfig', JSON.stringify(config));
        } catch (e) {
            console.error(e);
            throw e;
        }
    }

    async convertDivToImage() {
        this.printing = true;
        this.prepareSchemeForPrinting();

        const image = await this.printImageOfDiv(this.containerDiv());

        this.printService.image = image;

        this.printService.printScene.complete();

        this.finaliseSchemeAfterPrinting();
    }

    async convertDivToImages(): Promise<void> {
        this.printing = true;
        this.prepareSchemeForPrinting();

        let images = [];

        const start = await this.printImageOfDiv(this.startRef());
        images.push(start);

        await Promise.all(
            this.taskRefs().map(async taskRef => {
                const image = await this.printImageOfDiv(taskRef);
                images.push(image);
            })
        );

        const end = await this.printImageOfDiv(this.endRef());
        images.push(end);

        this.printService.images = images;

        this.printService.printDetailedScene.complete();

        this.finaliseSchemeAfterPrinting();
    }

    private async printImageOfDiv(divRef: ElementRef): Promise<HTMLImageElement> {
        const divElement = divRef.nativeElement;

        const canvas = await html2canvas(divElement);

        const pngData = canvas.toDataURL('image/png');
        const image = new Image();
        image.src = pngData;
        return image;
    }

    private prepareSchemeForPrinting(): void {
        this.originalScheme = this.scheme;
        if (this.scheme === 'dark' || this.scheme === 'auto') {
            this.changeScheme();
        }
    }

    private finaliseSchemeAfterPrinting(): void {
        setTimeout(() => {
            this.selectedId = this._selectedId;
            if (this.originalScheme === 'dark') {
                this.changeScheme();
            }
            this.printing = false;
        }, 200);
    }
}
